/** @jsx jsx */
import { Grid, Spacer } from "@cd2/components"
import { css, jsx } from "@emotion/core"
import { useHistory } from "react-router-dom"
import { breadCrumbs } from "../../constants/breadCrumbs"

import { useFetchMe } from "../../hooks/useFetchMe"
import { LoadingScreen } from "../../interfaceParts/LoadingScreen"
import { ICrumb, PageWrapper } from "../../interfaceParts/PageWrapper"
import { PageNotFoundPage } from "../admin/PageNotFoundPage"
import { eRoutes, routes } from "../../routes"
import { icons } from "../../utils/icons"
import { useBillingStatus } from "../../providers/BillingStatusProvider"
import { ErrorBoundary } from "../../ErrorBoundary"
import { CGButton } from "../../interfaceParts/CGButton"
import { useTheme } from "@cd2/theme"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useState } from "react"
import { useGlobalState } from "../../utils/globalState"
import { titleize } from "../../utils/string"

export function CompanySettingsPageWrapper({
  active,
  children,
  breadcrumb,
}: {
  active: string
  children: React.ReactNode
  breadcrumb?: ICrumb | ICrumb[]
}) {
  const history = useHistory()
  const { themeColor } = useTheme()
  const { billingData } = useBillingStatus()
  const user = useFetchMe()
  const canManageCompany = user?.isManager || user?.isOwner || user?.isAdmin

  const globalState = useGlobalState()
  const [helpRequestCount, setHelpRequestCount] = useState<number>(0)
  const [subCompanyCount, setSubCompanyCount] = useState<number | undefined>(undefined)

  useAsyncEffect(async () => {
    const helpRequestCount = await window.skeem.fetch2<number>({
      count: {
        name: "helpRequests",
        matches: { targetCompany: globalState.companyId, actioned: false },
      },
    })
    setHelpRequestCount(helpRequestCount)
  }, [globalState.companyId])

  useAsyncEffect(async () => {
    if (!globalState.company.isHeadCompany) return
    const count = await window.skeem.fetch2<number>({
      count: {
        name: "companies",
        matches: { headCompany: globalState.companyId, archived: false },
      },
    })
    setSubCompanyCount(count)
  }, [globalState.companyId, globalState.company.isHeadCompany])

  const title = globalState.company.name || "-"

  if (!user || !billingData) {
    return <LoadingScreen />
  }

  const knowledgeBaseOnly =
    globalState.settings.useKnowledgeBase && globalState.settings.knowledgeBaseOnly

  if (!canManageCompany) {
    return <PageNotFoundPage />
  }

  const tabs = !canManageCompany
    ? []
    : !billingData.canAccessSystem
    ? [
        {
          icon: "file-invoice-dollar",
          key: "billing",
          title: "Plans and Payment",
          to: `/manage-team/billing`,
        },
      ]
    : [
        {
          icon: icons.stats,
          key: "stats",
          title: "Stats",
          to: "/manage-team",
        },
        {
          icon: "edit",
          key: "details",
          title: "Company details",
          to: "/manage-team/details",
        },
        {
          icon: icons.users,
          key: "users",
          title: "Users",
          to: eRoutes["/manager/users"],
        },
        {
          icon: icons.employees,
          key: "employees",
          title: "Employees",
          hide: knowledgeBaseOnly || globalState.settings.disableEmployeeFeatures,
          to: eRoutes["/manager/employees"],
        },
        {
          icon: icons.subCompanies,
          key: "sub-companies",
          title:
            subCompanyCount !== undefined ? `Sub companies (${subCompanyCount})` : "Sub companies",
          to: "/manage-team/sub-companies",
          hide: !globalState.company.isHeadCompany,
        },
        {
          icon: icons.teams,
          key: "teams",
          title: "Teams",
          to: "/manage-team/teams",
          hide: globalState.company.isSubCompany,
        },
        {
          icon: "tasks",
          key: "tasks",
          title: "User Tasks",
          to: "/manage-team/tasks",
        },
        {
          icon: icons.departments,
          key: "departments",
          title: "Departments",
          hide: knowledgeBaseOnly || !globalState.settings.useRota,
          to: routes.companyManageDepartments,
        },
        {
          icon: "tags",
          key: "employee-tags",
          title: "Employee Tags",
          hide: knowledgeBaseOnly || globalState.settings.disableEmployeeFeatures,
          to: "/manage-team/employee-tags",
        },
        {
          icon: "cog",
          key: "settings",
          title: "Platform Functionality",
          hide: knowledgeBaseOnly,
          to: `/manage-team/settings`,
        },
        {
          icon: "shield-alt",
          key: "sso",
          title: "SSO",
          to: "/manage-team/sso",
          hide: globalState.company.isSubCompany,
        },
        {
          icon: icons.helpRequests,
          key: "help-requests",
          title: "Help Requests",
          hide: knowledgeBaseOnly,
          to: eRoutes[`/manage-team/help-requests`],
          count: helpRequestCount,
        },
        {
          icon: "tags",
          key: "manageTags",
          title: "Manage Alerts",
          to: "/manage-team/alerts",
        },
        {
          icon: "wrench",
          key: "updates",
          title: "Manage Updates",
          to: "/manage-team/updates",
          hide: knowledgeBaseOnly,
        },
        {
          icon: "file-alt",
          key: "files",
          title: "Manage Files",
          disabled: !canManageCompany,
          to: eRoutes[`/manage-team/files`],
          hide: knowledgeBaseOnly,
        },
        {
          icon: "briefcase",
          key: "manageFlows",
          title: "Manage Processes",
          to: "/manage-team/manageFlows",
          hide: knowledgeBaseOnly,
        },
        {
          icon: icons.link,
          key: "links",
          title: "Internal Resources",
          to: "/manage-team/links",
        },
        {
          icon: "user-tie",
          key: "hrProfiles",
          title: "HR profiles",
          disabled: !canManageCompany,
          to: "/manage-team/hrProfiles",
          hide: !globalState.settings.canSeeHrTeamsArea || knowledgeBaseOnly,
        },
        {
          icon: icons.whiteLabelling,
          key: "appearance",
          title: "Appearance",
          hide: !globalState.settings.useWhiteLabelling,
          to: "/manage-team/appearance",
        },
        {
          icon: "history",
          key: "history",
          title: "History",
          to: "/manage-team/history",
        },
        {
          icon: icons.locations,
          key: "location",
          title: "Locations",
          to: "/manage-team/location",
          hide: !globalState.settings.useLocations || knowledgeBaseOnly,
        },
        {
          icon: "file-invoice-dollar",
          key: "billing",
          title: globalState.settings.enableBillingDashboard ? "Billing" : "Plans and Payment",
          to: `/manage-team/billing`,
          hide: globalState.company.isSubCompany,
        },
        {
          icon: "receipt",
          key: "billing-history",
          title: "Billing History",
          to: `/manage-team/billing-history`,
          hide: globalState.settings.enableBillingDashboard || globalState.company.isSubCompany,
        },
        {
          icon: icons.fourth,
          key: "fourth",
          title: "Fourth Integration",
          to: eRoutes["/manage-team/fourth"],
          hide: !globalState.settings.enableFourthIntegration,
        },
        {
          icon: icons.link,
          key: "itrent",
          title: "iTrent Integration",
          to: eRoutes["/manage-team/itrent"],
          hide: !globalState.settings.enableITrentIntegration || !globalState.company.isHeadCompany,
        },
      ]
        .filter(Boolean)
        .filter(el => !el.hide)

  return (
    <PageWrapper
      title={title}
      breadcrumbs={[
        breadCrumbs.dashboard,
        breadCrumbs.userCompanySettings,
        ...(breadcrumb
          ? Array.isArray(breadcrumb)
            ? breadcrumb
            : [breadcrumb]
          : [{ name: titleize(active), to: "#" }]),
      ]}
    >
      <Spacer size={32} />
      <div>
        <Grid columns={[180, "auto"]} gap={32} alignItems="start">
          <Grid gap={6} alignItems="center" columns={["auto"]}>
            {tabs
              .filter(tab => !tab.hide)
              .map(tab => (
                <div
                  key={tab.key}
                  css={css`
                    position: relative;
                    border: initial !important;
                    border-bottom: 2px solid
                      ${tab.key === active ? themeColor("primary") : "transparent"} !important;
                    button {
                      div {
                        margin: 0;
                        display: block;
                      }
                    }
                  `}
                >
                  <CGButton
                    children={tab.title}
                    buttonStyle="text"
                    onClick={() => history.push(tab.to)}
                    extendTheme={{ padding: 7, fontSize: 12, borderRadius: 2 }}
                    leftIcon={tab.icon}
                    disabled={tab.disabled}
                    block
                  />
                  {Boolean(tab.count) && (
                    <div
                      css={css`
                        position: absolute;
                        right: 0;
                        top: 50%;
                        translate: -50% -50%;
                        background: red;
                        color: white;
                        font-weight: bold;
                        height: 24px;
                        width: 24px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                      `}
                    >
                      {tab.count}
                    </div>
                  )}
                </div>
              ))}
          </Grid>
          <ErrorBoundary>
            <div
              css={css`
                overflow: auto;
              `}
            >
              {children}
            </div>
          </ErrorBoundary>
        </Grid>
      </div>
    </PageWrapper>
  )
}
